// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import apiFetch from '@wordpress/api-fetch';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { confirm } from '@automator/shared/utils/confirm';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware( UncannyAutomatorBackend.rest.nonce ) );

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-loop-status-in-progress-cancel' )
export class AutomatorLogItemLoopStatusInProgressCancel extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Number, attribute: 'loop-id' } ) accessor loopID;

	// A unique code created to detect this specific loop run in this specific recipe run
	// The backend will use this to cancel/pause/resume the loop
	@property( { type: String, attribute: 'loop-process-code' } ) accessor processCode;

	@property( { type: String, attribute: false } ) accessor fatalErrorMessage;

	@property( { type: Boolean } ) accessor loading = false;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			${ this._templateButton }
			${ this._templateError }
		`;
	}

	/**
	 * Handles the resend functionality
	 */
	async handleResend() {
		// Check if it's loading, then abort
		if ( this.loading ) {
			return;
		}

		// Show confirmation dialog
		await confirm( {
			content: html`
				<uo-alert type="warning">
					${ escapeHTML( __( "You won't be able to resume this loop later.", 'uncanny-automator' ) ) }
				</uo-alert>
			`
		} );

		// As soon as the call starts, set the loading animation
		this.loading = true;
		this.fatalErrorMessage = '';

		// Start call
		apiFetch( {
			url: `${ UncannyAutomatorBackend.rest.url }/loop/process/cancel`,
			method: 'POST',
			data: {
				// eslint-disable-next-line camelcase
				process_id: this.processCode
			}
		} )
			.then( ( response ) => {
				// Stop loading animation
				this.loading = false;

				// Request data refresh
				this.requestDataRefresh();
			} )
			.catch( error => {
				// Show a global error
				this.fatalErrorMessage = ! isEmpty( error.message ) ? error.message : escapeHTML( __( 'Unknown error', 'uncanny-automator' ) );

				// Stop loading animation
				this.loading = false;

				// Request data refresh
				this.requestDataRefresh();

				console.error( error );
			} );
	}

	/**
	 * Send request to main component to refresh the data
	 */
	requestDataRefresh() {
		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-data-refresh', {
				bubbles: true,
				composed: true,
			} )
		);
	}

	/**
	 * Creates the cancel button.
	 * It'll create a disabled button if it's import mode
	 *
	 * @return {TemplateResult} The cancel button
	 */
	get _templateButton() {
		// Check if it's import mode
		if ( this.isImportMode ) {
			return html`
				<uo-tooltip>
					${ escapeHTML( __( 'Cancelling a loop is not possible in import mode', 'uncanny-automator' ) ) }

					<uo-button
						slot="target"
						size="small"
						color="danger"
						disabled
					>
						<uo-icon id="xmark"></uo-icon> ${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-tooltip>
			`;
		}

		return html`
			<uo-button
				size="small"
				color="danger"

				?loading=${ this.loading }

				@click=${ this.handleResend }
			>
				<uo-icon id="xmark"></uo-icon> ${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
			</uo-button>
		`;
	}

	/**
	 * Shows the error (if any)
	 *
	 * @return {TemplateResult|undefined} The error message
	 */
	get _templateError() {
		// Check if there is an error
		if ( isEmpty( this.fatalErrorMessage ) ) {
			return;
		}

		return html`
			<div class="error">${ this.fatalErrorMessage }</div>
		`;
	}
}
